import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import TableFooter from '@/components/TableFooter/TableFooter'
import {
  type LicenseStatus,
  useListOrgLicenses,
} from '@/features/Licenses/api/licenseHooks'
import { CreateLicenseDialog } from '@/features/Licenses/components/CreateLicenseDialog'
import { LicenseOriginBadge } from '@/features/Licenses/components/LicenseOriginBadge'
import { LicenseStatusBadge } from '@/features/Licenses/components/LicenseStatusBadge'
import { useCanManageLicenses } from '@/features/Licenses/hooks/useCanManageLicenses'
import { licenseDetailRoute } from '@/features/Licenses/pages/LicenseDetailPage'
import {
  Box,
  Button,
  HStack,
  Icon,
  Portal,
  Select,
  Skeleton,
  Table,
  Text,
  VStack,
  createListCollection,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ListFilter, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const LICENSES_PAGE_LIMIT = 50

// Labels read as words ("Active"), values stay the API's screaming-snake
// enum ("ACTIVE") — the filter is prose to the operator, protocol to the API.
const STATUS_FILTER_COLLECTION = createListCollection({
  items: [
    { label: 'All', value: '' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Expired', value: 'EXPIRED' },
    { label: 'Revoked', value: 'REVOKED' },
  ],
})

/**
 * SLG License list surfaced on Org Details.
 *
 * Renders `null` for viewers that can't manage licenses so operators
 * see zero new UI. For capable viewers: section header with a "Create
 * license" button, a table of existing licenses, and an empty state
 * when the org has no licenses yet.
 *
 * Spec reference: openspec/changes/add-slg-m1-backoffice-ui/specs/
 * license-list/spec.md. M1 is list + create only — no edit / revoke.
 */

type Props = {
  orgId: string
  page: number
  onPageChange: (page: number) => void
  licensesStatus?: LicenseStatus
  onStatusChange?: (status: LicenseStatus | undefined) => void
}

// en-US thousands-separated integers (5000 → "5,000") and date-only UTC
// rendering. The table previously rendered ISO timestamps and bare ints
// which was hard to scan for ops users.
const NUMBER_FORMAT = new Intl.NumberFormat('en-US')
const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

function formatNumber(value: number): string {
  return NUMBER_FORMAT.format(value)
}

function formatDate(epochMs: number): string {
  if (typeof epochMs !== 'number' || Number.isNaN(epochMs)) {
    return ''
  }
  return DATE_FORMAT.format(new Date(epochMs))
}

export function LicensesCard({
  orgId,
  page,
  onPageChange,
  licensesStatus,
  onStatusChange,
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  // While the roles query is in flight `canManage` reads false, so the card
  // stays hidden and the list query stays disabled. That self-corrects on the
  // re-render that follows the roles arriving — no `isResolved` gate needed
  // here because nothing irreversible happens on a denial.
  const { canManage } = useCanManageLicenses()
  const [isDialogOpen, setDialogOpen] = useState(false)
  const query = useListOrgLicenses(
    {
      pathParams: { orgId },
      queryParams: {
        limit: LICENSES_PAGE_LIMIT,
        offset: page * LICENSES_PAGE_LIMIT,
        ...(licensesStatus ? { status: licensesStatus } : {}),
      },
    },
    { enabled: canManage },
  )

  if (!canManage) {
    return null
  }

  const licenses = query.data?.items ?? []

  return (
    <>
      <SectionCard
        title={t('licenses.licensesCard.title')}
        actions={
          <>
            {onStatusChange ? (
              // No "Status" label: the collection's own placeholder ("All")
              // and its options name the dimension well enough, and a label
              // would crowd the heading row.
              <Select.Root
                collection={STATUS_FILTER_COLLECTION}
                size="xs"
                width="140px"
                aria-label={String(t('licenses.filters.status.label'))}
                value={[licensesStatus ?? '']}
                onValueChange={(details) => {
                  const value = details.value[0]
                  if (!value) {
                    onStatusChange(undefined)
                  } else {
                    onStatusChange(value as LicenseStatus)
                  }
                }}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger>
                    <HStack gap={2}>
                      {/* Carries the "this narrows the list" meaning the
                          removed "Status" label used to. */}
                      <Icon as={ListFilter} boxSize={4} color="fg.muted" />
                      <Select.ValueText
                        placeholder={String(t('licenses.filters.status.all'))}
                      />
                    </HStack>
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content>
                      {STATUS_FILTER_COLLECTION.items.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            ) : null}
            <Button
              colorPalette="brand"
              size="xs"
              onClick={() => setDialogOpen(true)}
            >
              <Plus size={14} />
              {t('licenses.licensesCard.createButton')}
            </Button>
          </>
        }
      >
        <VStack align="stretch" gap={4}>
          {query.isLoading && <Skeleton h={10} />}

          {!query.isLoading && query.isError && (
            <ApiErrorState
              apiResource={query as unknown as UseQueryResult}
              errorTitle={t('licenses.licensesCard.error.title')}
              errorDescription={t('licenses.licensesCard.error.description')}
            />
          )}

          {!query.isLoading && !query.isError && licenses.length === 0 && (
            <Text fontSize="sm" color="text.text-subtle">
              {t('licenses.licensesCard.emptyState')}
            </Text>
          )}

          {!query.isLoading && !query.isError && licenses.length > 0 && (
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>
                    {t('licenses.licensesCard.columns.name')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t('licenses.licensesCard.columns.status')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t('licenses.licensesCard.columns.package')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t('licenses.licensesCard.columns.entitlements')}
                  </Table.ColumnHeader>
                  {/* Two columns rather than one "start → end" cell: dates in
                      their own columns line up down the table, so scanning
                      "which of these expires soonest" is a read down one
                      column instead of picking the second half out of every
                      row. */}
                  <Table.ColumnHeader>
                    {t('licenses.licensesCard.columns.validFrom')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t('licenses.licensesCard.columns.validUntil')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t('licenses.licensesCard.columns.origin')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t('licenses.licensesCard.columns.reference')}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {licenses.map((lic) => {
                  // Trim defensively at the list site: it's our highest-
                  // exposure read surface (many rows, often glanced at by
                  // operators), and the server contract doesn't promise
                  // stored names are trimmed. Detail surfaces
                  // (MetadataPanel, LicenseDetailPage, RevokeForm) use
                  // `?? fallback` since they're single-record and any
                  // weird value is more visible to the operator there.
                  const displayName = lic.name?.trim()
                  return (
                    <Table.Row
                      key={lic.id}
                      position="relative"
                      cursor="pointer"
                      _hover={{ bg: 'bg.muted' }}
                    >
                      <Table.Cell>
                        {/*
                          Full-row overlay anchor. The Link is absolutely
                          positioned with inset 0; its closest positioned
                          ancestor is Table.Row (no other relatives in
                          between), so the anchor spans the entire row.
                          Interactive controls (DataTag copy button) sit
                          at zIndex 1 to receive their own clicks naturally.
                        */}
                        <Link
                          to={licenseDetailRoute.to}
                          params={{ orgId, licenseId: lic.id }}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 0,
                          }}
                          aria-label={`Open license ${displayName || lic.licenseKey}`}
                        />
                        <Box
                          position="relative"
                          zIndex={1}
                          display="inline-block"
                          maxW="100%"
                        >
                          {displayName ? (
                            <Text>{displayName}</Text>
                          ) : (
                            // Unnamed licences fall back to the key as a plain
                            // truncated string. It was a copyable DataTag, but
                            // a full key is too long to scan in a list column
                            // and the copy button competed with the row link —
                            // the detail page one click away has the full,
                            // copyable value.
                            <Text
                              fontFamily="mono"
                              fontSize="xs"
                              maxW="16rem"
                              whiteSpace="nowrap"
                              overflow="hidden"
                              textOverflow="ellipsis"
                            >
                              {lic.licenseKey}
                            </Text>
                          )}
                        </Box>
                      </Table.Cell>
                      <Table.Cell>
                        <LicenseStatusBadge status={lic.status} />
                      </Table.Cell>
                      <Table.Cell fontSize="sm">
                        {lic.package ?? '—'}
                      </Table.Cell>
                      <Table.Cell>
                        {`${formatNumber(lic.entitlements.maxConnections)} conn · ${formatNumber(lic.entitlements.maxMessagesPerSecond)} msg/s`}
                      </Table.Cell>
                      <Table.Cell fontSize="sm" whiteSpace="nowrap">
                        {formatDate(lic.validityStart)}
                      </Table.Cell>
                      <Table.Cell fontSize="sm" whiteSpace="nowrap">
                        {formatDate(lic.validityEnd)}
                      </Table.Cell>
                      <Table.Cell fontSize="sm">
                        {lic.origin ? (
                          <LicenseOriginBadge
                            origin={lic.origin}
                            position="relative"
                            zIndex={1}
                          />
                        ) : (
                          '—'
                        )}
                      </Table.Cell>
                      <Table.Cell fontSize="sm" fontFamily="mono">
                        {lic.reference ? (
                          // Same truncation treatment as the licenseKey
                          // fallback above: reference can hold up to 255
                          // chars (a Salesforce opportunity URL) and this is
                          // the last column, so it needs a visible ellipsis
                          // rather than silent clipping by the card's
                          // overflow:hidden. The Box wrapper (position/zIndex
                          // + inline-block/maxW=100%) matches the Name cell
                          // above: inline-block shrinks the raised,
                          // non-navigating area to the text itself, so only
                          // the value opts out of the row link, not the
                          // whole column.
                          <Box
                            position="relative"
                            zIndex={1}
                            display="inline-block"
                            maxW="100%"
                          >
                            <Text
                              maxW="16rem"
                              whiteSpace="nowrap"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              title={lic.reference}
                            >
                              {lic.reference}
                            </Text>
                          </Box>
                        ) : (
                          '—'
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          )}

          {(() => {
            const total = query.data?.total ?? 0
            const totalPages = Math.max(
              1,
              Math.ceil(total / LICENSES_PAGE_LIMIT),
            )
            if (total <= LICENSES_PAGE_LIMIT) {
              return null
            }
            return (
              <TableFooter
                page={page + 1}
                totalPages={totalPages}
                hasNextPage={(page + 1) * LICENSES_PAGE_LIMIT < total}
                onFirstPage={() => onPageChange(0)}
                onPreviousPage={() => onPageChange(page - 1)}
                onNextPage={() => onPageChange(page + 1)}
                onLastPage={() => onPageChange(totalPages - 1)}
              />
            )
          })()}
        </VStack>
      </SectionCard>

      <CreateLicenseDialog
        orgId={orgId}
        isOpen={isDialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  )
}
